import { z } from "zod";

/**
 * LA VUE HTML ISOLEE D'UNE FICHE, deposee par le MCP (`docs/socle/vues-html.md`).
 *
 * Une page HTML (JavaScript compris) qu'un administrateur fait ecrire par
 * Claude ou Codex, rendue dans un cadre FERME (`VueHtmlIsolee` de `@taki/ui` :
 * origine opaque, ni session, ni reseau) qui recoit la fiche ouverte, en
 * lecture seule, reduite aux champs que la vue DECLARE. Elle vit dans la
 * declaration de l'app de l'org (`ecran.vuesHtml`, lue par
 * `app-screens.ts`), donc `org:changements` la ramene dans le depot.
 *
 * Elle se declare PAR JEU : elle montre une fiche de ce jeu dans toute porte
 * qui le montre (une porte peut restreindre ses vues de fiche par
 * `vuesFiche`). Les trois outils du MCP qui l'ecrivent (`vue_contrat`,
 * `vue_proposer`, `vue_publier`) tiennent ces bornes, et le serveur les
 * RECOPIE (`functions/src/ia/vues-html.ts`) : `functions/` ne depend d'aucun
 * `@taki/*`.
 */

/** La version du protocole du cadre (`VueHtmlIsolee`) : une v2 s'ajoute, la v1 reste servie. */
export const VUE_HTML_PROTOCOLE = 1;
/** 128 Ko au plus : ce qui passe par un appel d'outil MCP, sans grosse bibliotheque collee dedans. */
export const VUE_HTML_TAILLE_MAX_OCTETS = 128 * 1024;
/**
 * LA BORNE D'UN ECRAN : ses vues HTML vivent dans le MEME document que toute
 * la declaration de l'app (`orgs/{orgId}/apps/{appId}`), qu'une revision
 * recopie (`appRevisions`), et Firestore refuse un document de plus de 1 Mio.
 * 10 vues et 384 Ko en tout laissent plus de 600 Ko au reste de la
 * declaration ; et une lecture (`read_app_declaration`, `studio_read_app`)
 * ne porte JAMAIS leur code (le serveur les resume, `functions/src/ia/vues-html.ts`),
 * donc reste loin des 512 Ko d'une reponse d'outil.
 */
export const VUE_HTML_PAR_ECRAN_MAX = 10;
export const VUE_HTML_TOTAL_MAX_OCTETS = 384 * 1024;
/** Le libelle du selecteur de vues : court (`docs/STYLE-LIBELLES.md`). */
export const VUE_HTML_LIBELLE_MAX = 40;
/** Les champs qu'une vue peut lire, au plus. */
export const VUE_HTML_CHAMPS_MAX = 100;
/** Les liens de la porte qu'une vue peut recevoir, au plus. */
export const VUE_HTML_LIENS_MAX = 20;
/** Un brouillon `vue_proposer` vit une heure, comme une proposition du Studio. */
export const VUE_HTML_BROUILLON_DUREE_MS = 60 * 60 * 1000;
/** La collection des brouillons, sous l'org : lus par leur seul auteur, ecrits par le serveur seul. */
export const VUE_HTML_BROUILLONS = "vuesBrouillons";
/** Le prefixe de l'id d'une vue HTML dans le selecteur des vues de fiche. */
export const VUE_HTML_PREFIXE = "html.";

/** L'id d'une vue HTML : `html.<slug>`, lisible par le schema des vues (`<espace>.<vue>`). */
export const VUE_HTML_ID_RE = /^html\.[a-z][a-z0-9-]{0,58}$/;

const NOM_DE_CHAMP_RE = /^(?:socle:)?[a-z][a-zA-Z0-9_]*$/;
const ID_DE_LIEN_RE = /^[a-z][a-z0-9-]*$/;

/** Le poids d'une page en octets UTF-8, celui que borne le serveur. */
export function tailleDeVueHtml(html: string): number {
  return new TextEncoder().encode(html).length;
}

/**
 * L'id d'une vue tire de son libelle : « Tableau de bord » -> `html.tableau-de-bord`.
 * Accents retires, tout ce qui n'est ni lettre ni chiffre devient un tiret.
 * Un libelle qui ne donne rien (que des signes) donne `html.vue`.
 */
export function idDeVueHtml(libelle: string): string {
  const slug = libelle
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 58)
    .replace(/-+$/g, "");
  const nom = slug === "" ? "vue" : /^[a-z]/.test(slug) ? slug : `v-${slug}`.slice(0, 59);
  return `${VUE_HTML_PREFIXE}${nom}`;
}

/**
 * UNE VUE HTML PUBLIEE, telle que `vue_publier` l'ecrit dans
 * `ecran.vuesHtml` de la declaration de l'org.
 *
 * - `champs` : les NOMS DE CHAMP du jeu que la vue lit, dans le vocabulaire
 *   des colonnes (`socle:status` pour un natif, la clef nue d'un champ
 *   declare). Le cadre ne recoit QUE ceux-la ; la verification du Studio
 *   signale une vue qui lit un champ disparu.
 * - `liens` : les ids des liens de la porte (`portes[].liens[].id`) dont la vue
 *   recoit les fiches liees.
 * - `version` : 1 a la premiere publication, + 1 a chaque nouvelle ; l'ancienne
 *   reste dans le journal des revisions de l'instance (`appRevisions`).
 */
export const vueHtmlDeclareeSchema = z
  .object({
    id: z.string().regex(VUE_HTML_ID_RE, "l'id d'une vue HTML s'écrit html.<nom>, en minuscules"),
    label: z.string().trim().min(1).max(VUE_HTML_LIBELLE_MAX),
    jeu: z
      .object({
        family: z.string().min(1),
        type: z.string().min(1).optional(),
      })
      .strict(),
    champs: z
      .array(z.string().regex(NOM_DE_CHAMP_RE, "un nom de champ est une clef déclarée, ou socle:<champ natif>"))
      .min(1)
      .max(VUE_HTML_CHAMPS_MAX),
    liens: z
      .array(z.string().regex(ID_DE_LIEN_RE, "un id de lien est en minuscules, tirets admis"))
      .max(VUE_HTML_LIENS_MAX)
      .optional(),
    html: z
      .string()
      .min(1)
      .refine((html) => tailleDeVueHtml(html) <= VUE_HTML_TAILLE_MAX_OCTETS, "une vue HTML pèse 128 Ko au plus"),
    version: z.number().int().min(1),
    /** L'instant de la publication, en ISO 8601 (la declaration est du JSON). */
    publieeLe: z.string().datetime({ offset: true }),
    /** L'uid de qui a publie. */
    publieePar: z.string().min(1).max(128),
  })
  .strict();

/** Une vue HTML publiee dans la declaration d'une app. */
export type VueHtmlDeclaree = z.infer<typeof vueHtmlDeclareeSchema>;

/** Le poids total des pages d'une liste de vues, en octets UTF-8. */
export function tailleDesVuesHtml(vues: readonly { html: string }[]): number {
  return vues.reduce((total, vue) => total + tailleDeVueHtml(vue.html), 0);
}

/**
 * LES VUES HTML D'UN ECRAN (`ecran.vuesHtml`) : chacune bornee, et la liste
 * aussi, en nombre et en poids total (`VUE_HTML_PAR_ECRAN_MAX`,
 * `VUE_HTML_TOTAL_MAX_OCTETS`). Le serveur tient les memes bornes a
 * `vue_proposer` et `vue_publier`, pour refuser avant d'ecrire.
 */
export const vuesHtmlDeclareesSchema = z
  .array(vueHtmlDeclareeSchema)
  .max(VUE_HTML_PAR_ECRAN_MAX, `un écran porte ${VUE_HTML_PAR_ECRAN_MAX} vues HTML au plus`)
  .refine((vues) => tailleDesVuesHtml(vues) <= VUE_HTML_TOTAL_MAX_OCTETS, "les vues HTML d'un écran pèsent 384 Ko au plus en tout");

/**
 * UN BROUILLON `vue_proposer`, lu par l'apercu (`sdk.vuesHtml.lireBrouillon`).
 * Ecrit par le serveur seul dans `orgs/{orgId}/vuesBrouillons/{id}`, lisible
 * de son seul auteur, une heure.
 */
export interface VueHtmlBrouillon {
  id: string;
  appId: string;
  /** La porte d'ou l'apercu s'ouvre. */
  porte: string;
  /** L'id de la vue (`html.<slug>`) ; deja publiee, la publication en est une mise a jour. */
  viewId: string;
  label: string;
  jeu: { family: string; type?: string };
  champs: string[];
  liens?: string[];
  html: string;
  /** La fiche sur laquelle l'apercu s'ouvre. */
  recordId: string | null;
  /** L'uid de l'auteur. */
  uid: string;
  expiresAt: Date | null;
}
